import {
  issueViewerPrimaryQueryKey,
  preheatIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useCachePreheatTasks} from '@github-ui/preheat-issues-manager/use-cache-preheat'
import {getQueryClient} from '@github-ui/query-client'
import {useAnalytics} from '@github-ui/use-analytics'
import {useEffect, useMemo} from 'react'

import {issueViewerSecondaryViewQueryOptions} from '../components/fetch-issue-viewer-secondary-view'
import {preheatIssueViewerSecondaryComponents} from '../components/preload-components'
import {useIssueViewerDataPreheat} from './use-issue-viewer-data-preheat'

type UseCachePreheatIssueViewerProps = {
  owner: string
  repo: string
  number: number
  allowedOwner?: string
  source: string
  enabled?: boolean
}

type UseCachePreheatIssueViewerQueriesProps = {
  issues: Array<{
    owner: string
    repo: string
    number: number
    allowedOwner?: string
  }>
  source: string
}

export function useCachePreheatIssueViewer({
  owner,
  repo,
  number,
  allowedOwner,
  source,
  enabled = true,
}: UseCachePreheatIssueViewerProps) {
  useIssueViewerDataPreheat({owner, repo, number, allowedOwner, source, enabled})

  useEffect(() => {
    if (!enabled) return
    void preheatIssueViewerSecondaryComponents()
  }, [enabled])
}

export function useCachePreheatIssueViewerQueries({issues, source}: UseCachePreheatIssueViewerQueriesProps) {
  const {sendAnalyticsEvent} = useAnalytics({optional: true})

  useEffect(() => {
    if (issues.length === 0) return
    void preheatIssueViewerSecondaryComponents()
  }, [issues.length])

  const secondaryPreheatTasks = useMemo(
    () =>
      issues.map(({owner, repo, number}) => {
        const options = issueViewerSecondaryViewQueryOptions({
          owner,
          repo,
          number,
          markAsRead: false,
          sendAnalyticsEvent,
          preheat: true,
          preheatSource: source,
        })
        return {
          key: JSON.stringify(options.queryKey),
          task: () => getQueryClient().prefetchQuery(options),
        }
      }),
    [issues, sendAnalyticsEvent, source],
  )

  const primaryPreheatTasks = useMemo(
    () =>
      issues.map(issue => ({
        key: JSON.stringify(issueViewerPrimaryQueryKey(issue)),
        task: () => preheatIssueViewerPrimary({...issue, source}),
        highPriority: true,
      })),
    [issues, source],
  )

  useCachePreheatTasks({tasks: primaryPreheatTasks})
  useCachePreheatTasks({tasks: secondaryPreheatTasks})
}
